import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { getItems } from '../api/inventoryMaster';
import useItemNames from '../hooks/useItemNames';
import useStoreItemClasses from '../hooks/useStoreItemClasses';
import {
  getItemId,
  itemOptionLabel,
  isItemActive,
  filterActiveItems,
  inactiveItemMessage,
} from '../utils/itemDisplay';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function normalizeItems(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

/**
 * Classes every restricted store allows, as CSV; '' when none is restricted.
 * null when the restricted stores share no class — kept apart from '', which
 * sends no filter and would return every item instead of none.
 */
function combinedClassFilter(scopes) {
  const restricted = scopes.filter(s => s.isRestricted);
  if (!restricted.length) return '';
  const [first, ...rest] = restricted;
  const shared = first.classIds.filter(id => rest.every(s => s.classIds.includes(id)));
  return shared.length ? shared.join(',') : null;
}

/**
 * Debounced searchable item picker for transaction screens (PO, RFQ, GRN,
 * requisition, transfer, opening stock). Inactive items are excluded from
 * results and cannot be selected.
 *
 * Pass `storeId` on store-scoped screens to restrict results to the item classes
 * that store allows. Omit it (the default) and class filtering is skipped.
 * Pass `supplyingStoreId` as well when the items come from another store (a
 * requisition's To Store) — results then cover only classes both stores allow.
 */
export default function InvItemAutocomplete({
  value,
  onChange,
  itemType,
  excludeMedicine = false,
  storeId,
  supplyingStoreId,
  label,
  placeholder = 'Search item…',
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const requestIdRef = useRef(0);
  const { catalog } = useItemNames();
  const ownClasses = useStoreItemClasses(storeId);
  const supplyingClasses = useStoreItemClasses(supplyingStoreId);
  const classesLoading = ownClasses.loading || supplyingClasses.loading;
  // A primitive, so the search effect can depend on it without re-firing on
  // every render. Forgetting this dep leaves a stale filter after a store change.
  const classFilter = combinedClassFilter([ownClasses, supplyingClasses]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem && getItemId(selectedItem) === value) return;
    const found = options.find((item) => getItemId(item) === value)
      || catalog?.byId?.get(value)
      || null;
    if (found && !isItemActive(found)) return;
    if (found) setSelectedItem(found);
  }, [value, options, selectedItem, catalog]);

  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!open) return;
    // Hold until the store's allow-list resolves, so a restricted store never
    // flashes unfiltered options.
    if (classesLoading) return;

    if (classFilter === null) {
      // Retire any in-flight search so it cannot repopulate the list.
      requestIdRef.current += 1;
      setOptions([]);
      setLoading(false);
      return;
    }

    const inputQuery = inputValue.trim();
    const query = debouncedSearch;
    const shouldSearch = query.length >= MIN_SEARCH_LENGTH;
    const pendingSearch = inputQuery.length >= MIN_SEARCH_LENGTH && inputQuery !== query;
    const shouldBrowse = !query && inputQuery.length < MIN_SEARCH_LENGTH;

    if (pendingSearch) {
      setOptions([]);
      return;
    }

    if (!shouldSearch && !shouldBrowse) {
      setOptions([]);
      return;
    }

    const requestId = ++requestIdRef.current;

    async function fetchItems() {
      if (shouldSearch) setOptions([]);
      setLoading(true);
      try {
        const params = { is_active: true };
        if (shouldSearch) params.search = query;
        if (itemType) params.item_type = itemType;
        // CSV, never an array: axios serializes arrays as `key[]=a&key[]=b`, and
        // the EMR gateway collapses repeated params to their last value. Either
        // would silently return the wrong items rather than erroring.
        if (classFilter) params.item_class_ids = classFilter;

        // API: GET /inventory/items/
        const data = await getItems(params);
        if (requestId !== requestIdRef.current) return;

        let items = filterActiveItems(normalizeItems(data));
        if (excludeMedicine) {
          items = items.filter((item) => item.item_type !== 'MEDICINE');
        }
        setOptions(items);
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }

    fetchItems();
  }, [debouncedSearch, open, itemType, excludeMedicine, inputValue, classFilter, classesLoading]);

  const displayOptions = useMemo(() => {
    const list = filterActiveItems(options);
    if (isSearching || !selectedItem) return list;
    const id = getItemId(selectedItem);
    if (list.some((item) => getItemId(item) === id)) return list;
    if (!isItemActive(selectedItem)) return list;
    return [selectedItem, ...list];
  }, [options, selectedItem, isSearching]);

  const noOptionsText = (() => {
    if (loading) return 'Searching…';
    if (classFilter === null) return 'No item class is allowed in both stores';
    if (inputValue.trim().length > 0 && inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    return 'No items found';
  })();

  return (
    <Autocomplete
      size="small"
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedItem
            && newInput.trim() !== itemOptionLabel(selectedItem)
          ) {
            setSelectedItem(null);
            onChange('', null);
          }
        }
      }}
      onChange={(_, item) => {
        if (item && !isItemActive(item)) {
          toast.error(inactiveItemMessage(item));
          return;
        }
        setSelectedItem(item);
        onChange(getItemId(item), item);
      }}
      getOptionLabel={itemOptionLabel}
      isOptionEqualToValue={(a, b) => getItemId(a) === getItemId(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={label ? { shrink: true } : undefined}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
